import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  computed,
} from '@angular/core';
import { NgTemplateOutlet, DatePipe } from '@angular/common';
import {
  Notification,
  NotificationTypeValue,
  NotificationFilterTab,
} from './notifications-type.enum';
import { ScrollBottomDirective } from '../../directives/scroll-bottom.directive';
import { TableFilterTab } from '../table/table.model';
import {
  DangerIconComponent,
  DirectBoxReactiveIconComponent,
  WorkflowIconComponent,
  SettingIconComponent,
  UserOctagonIconComponent,
  TickSquareIconComponent,
  ChevronRightIconComponent,
  NoNotificationIconComponent,
  UsersSolidIconComponent,
  MentionIconComponent,
  ResumeIconComponent,
  PendingIconComponent,
  NotificationStatusIconComponent,
  ReactionIconComponent,
  UserTwoIconComponent,
  UserNotificationIconComponent,
  ArrowUpCircleIconComponent,
  MoneyAddIconComponent,
  MoneysIconComponent,
  CardAddIconComponent,
  CardEditIconComponent,
} from '@kuiper/icons';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'lib-notification',
  imports: [
    NgTemplateOutlet,
    UsersSolidIconComponent,
    DangerIconComponent,
    DirectBoxReactiveIconComponent,
    WorkflowIconComponent,
    ChevronRightIconComponent,
    NoNotificationIconComponent,
    SettingIconComponent,
    TranslocoPipe,
    DatePipe,
    ScrollBottomDirective,
    UserOctagonIconComponent,
    TickSquareIconComponent,
    MentionIconComponent,
    ResumeIconComponent,
    PendingIconComponent,
    NotificationStatusIconComponent,
    ReactionIconComponent,
    UserTwoIconComponent,
    UserNotificationIconComponent,
    ArrowUpCircleIconComponent,
    MoneyAddIconComponent,
    MoneysIconComponent,
    CardAddIconComponent,
    CardEditIconComponent,
  ],
  templateUrl: './notification.component.html',
  styleUrl: './notification.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class NotificationComponent {
  NotificationTypeValue = NotificationTypeValue;
  NotificationFilterTab = NotificationFilterTab;
  notifications = input.required<Notification[]>();
  iconFill = input<string>('var(--color-destructive-500)');
  width = input<string>('auto');
  height = input<string>('auto');
  maxNotifications = input<number>(10);
  noStateIconSize = input<string>('size-16');
  noStateTitleSize = input<string>('text-lg');
  noStateDescriptionSize = input<string>('text-sm');
  hasTitleBottomBorder = input<boolean>(true);
  showHeader = input<boolean>(true);
  showTabs = input<boolean>(false);
  notificationFilterTabs = input<TableFilterTab[]>([]);

  notificationClick = output<Notification>();
  markAllAsRead = output<void>();
  settingsClick = output<void>();
  viewDetailsClick = output<Notification>();
  handleNotificationFilterTab = output<TableFilterTab>();
  scrolledBottom = output<void>();
  isLoading = input<boolean>(false);
  useScrollPagination = input<boolean>(true);

  activeTabKey = computed(() => {
    const activeTab = this.notificationFilterTabs().find(tab => tab.active);
    return activeTab ? activeTab.key : NotificationFilterTab.ALL;
  });

  sortedNotifications = computed(() => {
    let filtered = this.notifications();
    if (this.activeTabKey() === NotificationFilterTab.UNREAD) {
      filtered = filtered.filter(notification => !notification.read_at);
    }

    const sorted = filtered.slice().sort((a, b) => {
      return (
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    });
    return sorted;
  });

  onScrolledBottom(): void {
    if (!this.isLoading() && this.useScrollPagination()) {
      this.scrolledBottom.emit();
    }
  }

  onNotificationClick(notification: Notification) {
    this.notificationClick.emit(notification);
  }

  onMarkAllAsRead() {
    this.markAllAsRead.emit();
  }

  onTabChange(tab: TableFilterTab) {
    this.handleNotificationFilterTab.emit(tab);
  }

  onSettingsClick() {
    this.settingsClick.emit();
  }

  onViewDetailsClick(event: Event, notification: Notification) {
    event.stopPropagation();
    this.viewDetailsClick.emit(notification);
  }

  getIconBgClass(notification: Notification): string {
    if (notification.read_at) {
      return 'bg-neutral-100';
    }
    return notification.notification_type.value ===
      NotificationTypeValue.SLA_TIME_NOTIFICATION
      ? 'bg-destructive-100'
      : 'bg-primary-50';
  }

  hasUnreadMessages(): boolean {
    return this.notifications().some(notification => !notification.read_at);
  }

  isToday(timestamp: string): boolean {
    const date = new Date(timestamp);
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }
}
